import {getMonthEnd, getMonthStart} from '@fluentui/date-time-utilities';
import React, {CSSProperties, useMemo} from 'react';
import {Cell, CellProps} from 'react-table';
import styled from 'styled-components';
import {buildBackgroundImage} from '../common/calendar/backgroundImage';
import {DateRangeSelector} from '../common/calendar/DateRangeSelector';
import {EventBars, onClickEventFn} from '../common/calendar/EventBar';
import {MonthSelector} from '../common/calendar/MonthSelector';
import {DateRange} from '../common/DateRange';
import {
  CheckboxProps,
  ResourceDetailsContainer,
  ResourceDetailsTable,
  resourceDetailsTableNullColumn,
  StickyColumn,
} from '../common/ResourceDetailsTable';
import {COMMAND_BAR_HEIGHT} from '../consts';
import {Actions} from '../types/Action';
import {ResourceDetails} from '../types/ResourceDetails';
import {ResourceList} from '../types/ResourceList';
import {TableTool} from '../types/Table';
import {TableColumn} from '../types/TableColumn';
import {ToolButton} from '../types/ToolButton';
import {buildColumns} from './GridList';
import {Sorter} from '../types/Sorter';
import {buildCalendarMod, CalendarMod} from '../common/calendar/CalendarMod';
import {addMinutesToDate, formatDate, formatDateWithWeekdayForHuman,} from '../dates/dates';
import {OnChangeSorterFn} from '../common/table/SorterChanger';
import {Styles} from '../types/Styles';
import {selectFontColor} from '../common/calendar/color';
import {useBusinessDayStartTimeMinutes} from '../common/hooks/useBusinessDayStartTimeMinutes';
import {Spinner} from '../common/Spinner';

export type RowCalendarListProps = {
  type: string;
  schemaId: string;
  buttons?: ToolButton[];
  farButtons?: ToolButton[];
  sorters?: Sorter[];
  tableTools?: TableTool[];
  tableFarTools?: TableTool[];
  columns?: TableColumn[];
  allowAdjacent?: boolean;
  customDateRange?: boolean;
  customDateRangeMaxLength?: number;
  showMonthInHeaderDays?: boolean;
  checkbox?: boolean;
  unitWidth?: number;
  tree?: boolean;
  styles?: Styles;
};

type Props = {
  resourceList: ResourceList;
  items: ResourceDetails[];
  columns?: TableColumn[];
  events: {[itemId: string]: ResourceDetails};
  dateRange: DateRange;
  onChangeDateRange: (start: Date, end: Date) => void;
  onSelect: (item: ResourceDetails) => void;
  onClickEvent?: onClickEventFn;
  tableTools?: TableTool[];
  tableFarTools?: TableTool[];
  onChangePageSize: (size: number) => void;
  onChangeCurrentPage: (index: number) => void;
  onChangeSorter: OnChangeSorterFn;
  allowAdjacent?: boolean;
  customDateRange?: boolean;
  customDateRangeMaxLength?: number;
  showMonthInHeaderDays?: boolean;
  actions: Actions;
  unitWidth?: number;
  tree?: boolean;
  styles?: Styles;
} & CheckboxProps;

export function RowCalendarList(props: Props): JSX.Element {
  const minutes = useBusinessDayStartTimeMinutes();

  const mod = useMemo(() => {
    return buildCalendarMod(props.resourceList);
  }, [props.resourceList]);

  const eventColumns = buildEventColumns(props, minutes, mod);

  const cols = useMemo(() => {
    return buildFieldColumns(buildColumns(props.resourceList, props.columns));
  }, [props.resourceList, props.columns]);

  if (minutes === null) {
    return <Spinner />;
  }

  return (
    <ResourceDetailsTable
      expandLastColumn={true}
      {...props}
      columns={cols}
      columnsDecorator={(columns) => {
        return [
          ...columns.map((c) => {
            c.sticky = 'left';
            return c;
          }),
          ...eventColumns,
        ];
      }}
      headerDecorator={(header: JSX.Element) => {
        return (
          <HeaderContainer>
            {buildMonthSelector(props, minutes)}
            {header}
          </HeaderContainer>
        );
      }}
    />
  );
}

const defaultUnitWidth = 36;

function buildEventColumns(
  props: Props,
  businessDayStartTimeMinutes: number | null,
  mod?: CalendarMod,
): StickyColumn[] {
  if (businessDayStartTimeMinutes === null) {
    return [];
  }

  const unitWidth = props.unitWidth || defaultUnitWidth;

  const image = buildBackgroundImage(props.dateRange, unitWidth, mod);
  const style = {
    backgroundImage: `url(${image})`,
    ...props.styles?.headerDays,
  };

  return [
    {
      Header: () => {
        return buildDaysHeader(
          props.dateRange,
          props.showMonthInHeaderDays ?? false,
          style,
          unitWidth,
          mod,
        );
      },
      Cell: (cellProps: CellProps<ResourceDetailsContainer>) => {
        const contents = buildContents(
          props.events,
          props.dateRange,
          businessDayStartTimeMinutes,
          cellProps.cell,
          unitWidth,
          props.onClickEvent,
          props.allowAdjacent,
        );
        return <Days style={style}>{contents}</Days>;
      },
      width: unitWidth * props.dateRange.len,
      id: '__events__',
      accessor: (originalRow) => {
        return originalRow.item['__events__'];
      },
    },
    resourceDetailsTableNullColumn,
  ];
}

function buildFieldColumns(columns?: TableColumn[]): TableColumn[] {
  if (!columns || columns.length === 0) {
    return defaultColumns;
  }

  return columns;
}

const defaultColumns: TableColumn[] = [
  {
    fieldId: 'name',
    label: '名称',
    visible: true,
    width: 200,
    styles: {
      body: {
        whiteSpace: 'normal',
      },
    },
  },
];

function buildMonthSelector(
  props: Props,
  businessDateStartTimeMinutes: number,
): JSX.Element {
  return (
    <MonthSelectorContainer>
      {props.customDateRange ? (
        <DateRangeSelector
          dateRange={props.dateRange}
          onChangeDateRange={(range) => {
            props.onChangeDateRange(range.start, range.end);
          }}
          businessDateStartTimeMinutes={businessDateStartTimeMinutes}
          maxLength={props.customDateRangeMaxLength}
          style={{marginLeft: '0.5rem'}}
        />
      ) : (
        <MonthSelector
          month={new Date()}
          onSelect={(month: Date) => {
            const start = addMinutesToDate(
              getMonthStart(month),
              businessDateStartTimeMinutes,
            )!;
            const end = addMinutesToDate(
              getMonthEnd(month),
              businessDateStartTimeMinutes,
            )!;
            props.onChangeDateRange(start, end);
          }}
        />
      )}
    </MonthSelectorContainer>
  );
}

function buildDaysHeader(
  dateRange: DateRange,
  showMonth: boolean,
  style: CSSProperties,
  unitWidth: number,
  mod?: CalendarMod,
): JSX.Element {
  const days: JSX.Element[] = [];
  const todayText = formatDate(new Date());

  dateRange.eachDate((date, i) => {
    days.push(
      <Day
        key={`header-${i}`}
        date={date}
        todayText={todayText}
        showMonth={showMonth}
        unitWidth={unitWidth}
        mod={mod}
      />,
    );
  });

  return <Days style={style}>{days}</Days>;
}

type DayProps = {
  date: Date;
  todayText: string;
  showMonth: boolean;
  unitWidth: number;
  mod?: CalendarMod;
};

function Day({
  date,
  todayText,
  showMonth,
  unitWidth,
  mod,
}: DayProps): JSX.Element {
  const color = selectFontColor(date, todayText, mod);
  const title = buildTitle(date, mod);
  const d = date.getDate();

  if (!showMonth) {
    return (
      <HeaderDay unitWidth={unitWidth} color={color} title={title}>
        {d}
      </HeaderDay>
    );
  }

  const m = date.getMonth() + 1;

  return (
    <HeaderDay unitWidth={unitWidth} color={color} title={title}>
      {m}
      <Slash />
      {d}
    </HeaderDay>
  );
}

function Slash() {
  return <SlashWrapper>/</SlashWrapper>;
}

function buildTitle(date: Date, mod?: CalendarMod) {
  const title = formatDateWithWeekdayForHuman(date);

  if (!mod) {
    return title;
  }

  const ymd = formatDate(date);
  const sub = mod.dateMap[ymd]?.name;

  if (!sub) {
    return title;
  }

  return title + ' ' + sub;
}

const buildContents = (
  events: {[key: string]: ResourceDetails},
  dateRange: DateRange,
  businessDayStartTimeMinutes: number,
  cell: Cell<ResourceDetailsContainer>,
  unitWidth: number,
  onClickEvent?: onClickEventFn,
  allowAdjacent?: boolean,
) => {
  const eventIds: string[] = cell.row.original.item['events'] || [];
  const evts = eventIds.filter((id) => !!events[id]).map((id) => events[id]);

  return (
    <EventBars
      events={evts}
      onClickEvent={onClickEvent}
      dateRange={dateRange}
      unitWidth={unitWidth}
      allowAdjacent={allowAdjacent}
      businessDayStartTimeMinutes={businessDayStartTimeMinutes}
    />
  );
};

const HeaderContainer = styled.div`
  display: flex;
`;

const MonthSelectorContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: ${COMMAND_BAR_HEIGHT}px;
`;

type CalendarProps = {
  unitWidth: number;
  color: string;
};

function calendarStyle(props: CalendarProps) {
  return {
    style: {
      width: props.unitWidth + 'px',
      color: props.color,
    },
  };
}

const HeaderDay = styled.div.attrs<CalendarProps>(
  calendarStyle,
)<CalendarProps>``;

const Days = styled.div`
  display: flex;
  align-items: center;
  background-repeat: repeat;
  height: 100%;
  width: 100%;
`;

const SlashWrapper = styled.span`
  margin-left: 2px;
  margin-right: 2px;
`;
